/* eslint-disable react/no-unescaped-entities */
import { PageHero } from '../components/SectionHeading';
import { ScrollReveal } from '../components/Motion';
import { ASSETS, PARTNERS, pick } from '../data/content';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useLang } from '../i18n/LanguageContext';

const HISTORICAL_DETAIL_EN = {
    BP: { full: 'BP — British Petroleum', body: 'BP was Delta\'s co-investor and the lead operator of AIOC — the consortium formed to develop the Azeri, Chirag, and Gunashli (ACG) fields under the "Contract of the Century" PSA signed in 1994. BP held a 30.1% stake in AIOC as operator, with Delta participating as a founding consortium member. The ACG complex went on to produce more than 600,000 barrels per day.' },
    UNOCAL: { full: 'UNOCAL Corporation', body: 'UNOCAL was Delta\'s founding strategic alliance partner in Central Asia. In 1991, Delta and UNOCAL formed an alliance and jointly acquired a circa 12% interest in the ACG fields — the foundational transaction that positioned Delta as an early-mover in the Caspian.' },
    Pennzoil: { full: 'Pennzoil', body: 'A fellow founding member of the AIOC consortium, participating alongside Delta, BP, Amoco, UNOCAL, and SOCAR in the "Contract of the Century" PSA signed on September 20, 1994.' },
    'Amerada Hess': { full: 'Amerada Hess / Hess Corporation', body: 'Delta sold its AIOC interest to Amerada Hess in 1998, crystallizing significant value. Delta and Hess also participated jointly as Delta Hess in the early development of the Baku-Tbilisi-Ceyhan pipeline.' },
    SOCAR: { full: 'SOCAR — State Oil Company of Azerbaijan', body: 'SOCAR was Delta\'s sovereign partner in the AIOC consortium and a co-signatory of the "Contract of the Century" PSA in 1994. SOCAR held a 25% stake in BTC Co. and was the national anchor of the entire ACG development program.' },
    'Dana Gas PJSC': { full: 'Dana Gas PJSC', body: "Dana Gas became Delta's counterparty in the Centurion Energy divestment of 2007, launching a C$12.00 per share all-cash bid valuing Centurion at approximately C$1.25 billion — delivering a return of approximately 20x Delta's original 1999 acquisition price." },
};
const HISTORICAL_DETAIL_AR = {
    BP: { full: 'بي بي — برتش بتروليوم', body: 'كانت بي بي شريك دلتا والمشغل الرئيسي لـ AIOC — التحالف المُشكّل لتطوير حقول أذيري وشيراج وغوناشلي (ACG) بموجب اتفاقية تقاسم الإنتاج "عقد القرن" الموقعة عام 1994. امتلكت بي بي حصة 30.1٪ في AIOC كمشغل، بينما شاركت دلتا كعضو مؤسس. وصل إنتاج مجمع ACG إلى أكثر من 600,000 برميل يوميًا.' },
    UNOCAL: { full: 'شركة يونوكال', body: 'كانت يونوكال شريك التحالف الاستراتيجي المؤسس لدلتا في آسيا الوسطى. عام 1991، شكّلت دلتا ويونوكال تحالفًا واستحوذتا معًا على حصة تقارب 12٪ في حقول ACG — الصفقة الأساسية التي وضعت دلتا كمحرك مبكر في بحر قزوين.' },
    Pennzoil: { full: 'بنزويل', body: 'عضو مؤسس مشارك في تحالف AIOC، يشارك إلى جانب دلتا وبي بي وأموكو ويونوكال وسوكار في اتفاقية تقاسم الإنتاج "عقد القرن" الموقعة في 20 سبتمبر 1994.' },
    'Amerada Hess': { full: 'أمريدا هيس / هيس كوربوريشن', body: 'باعت دلتا حصتها في AIOC إلى أمريدا هيس عام 1998، محققة قيمة كبيرة. وشاركت دلتا وهيس أيضًا معًا تحت اسم دلتا-هيس في التطوير المبكر لخط أنابيب باكو-تبليسي-جيهان.' },
    SOCAR: { full: 'سوكار — شركة النفط الحكومية الأذربيجانية', body: 'كانت سوكار الشريك السيادي لدلتا في تحالف AIOC وموقعة مشاركة لاتفاقية "عقد القرن" عام 1994. امتلكت سوكار حصة 25٪ في شركة BTC وكانت المرتكز الوطني لكامل برنامج تطوير ACG.' },
    'Dana Gas PJSC': { full: 'شركة دانة غاز ش.م.ع', body: 'أصبحت دانة غاز الطرف المقابل لدلتا في التخارج من سنتوريون للطاقة عام 2007، إذ أطلقت عرضًا نقديًا بقيمة 12 دولارًا كنديًا للسهم بقيمة إجمالية تقارب 1.25 مليار دولار كندي — محققة عائدًا يقارب 20 ضعفًا من سعر الاستحواذ الأصلي لدلتا عام 1999.' },
};

const ACTIVE_DETAIL_EN = {
    Sonatrach: { full: 'Sonatrach — National Oil Company of Algeria', body: 'Partner for the Illizi South 30-year PSA signed in October 2025 — a landmark agreement under Algerian Law No. 19-13 covering ~993 MMboe of expected production over the contract life.' },
    CNPC: { full: 'CNPC — China National Petroleum Corporation', body: "China's largest oil and gas company with extensive Central Asian infrastructure experience. EPC capabilities for Delta's programs: large-diameter cross-border gas pipeline construction, upstream drilling in frontier environments, gas processing, and long-term pipeline operations." },
    Sinopec: { full: 'Sinopec — China Petroleum & Chemical Corporation', body: "One of the world's largest refining and petrochemical companies. Downstream engineering capabilities in gas processing, refinery construction, and petrochemical plants make Sinopec a key partner for Afghanistan and Venezuela downstream components." },
    'China Energy': { full: 'China Energy Investment Corporation', body: "The world's largest power generation company. EPC candidate for the 900 MW CCGT gas power cluster in Afghanistan and the Termobarrancas expansion (80 MW → 450 MW) in Venezuela." },
};
const ACTIVE_DETAIL_AR = {
    Sonatrach: { full: 'سوناطراك — شركة النفط الوطنية الجزائرية', body: 'شريك في اتفاقية تقاسم إنتاج إليزي الجنوبي لمدة 30 عامًا الموقعة في أكتوبر 2025 — اتفاقية تاريخية بموجب القانون الجزائري رقم 19-13 تغطي إنتاجًا متوقعًا يبلغ ~993 مليون برميل مكافئ نفطي على مدى عمر العقد.' },
    CNPC: { full: 'CNPC — شركة البترول الوطنية الصينية', body: 'أكبر شركة نفط وغاز في الصين تمتلك خبرة واسعة في البنية التحتية لآسيا الوسطى. قدرات EPC لبرامج دلتا: بناء أنابيب غاز عابرة للحدود ذات أقطار كبيرة، حفر القطاع العلوي في البيئات الناشئة، معالجة الغاز، وعمليات الأنابيب طويلة الأمد.' },
    Sinopec: { full: 'Sinopec — شركة الصين للبترول والكيماويات', body: 'إحدى أكبر شركات التكرير والبتروكيماويات في العالم. تجعل قدرات الهندسة في القطاع السفلي في معالجة الغاز وبناء المصافي ومحطات البتروكيماويات من Sinopec شريكًا رئيسيًا لمكونات القطاع السفلي في أفغانستان وفنزويلا.' },
    'China Energy': { full: 'China Energy Investment Corporation', body: 'أكبر شركة لتوليد الطاقة في العالم. مرشحة EPC لمجمع طاقة الغاز CCGT بقدرة 900 ميغاواط في أفغانستان وتوسعة تيرموبارانكاس (80 ميغاواط → 450 ميغاواط) في فنزويلا.' },
};

const SUMMARY_EN = [
    ['BP', 'International Oil Major', '1994–1998', 'AIOC Co-investor & Operator — ACG Fields, Caspian'],
    ['UNOCAL', 'US Independent', '1991–1998', 'Strategic Alliance — ACG Fields entry; AIOC founding'],
    ['Pennzoil', 'US Oil Major', '1994–1998', 'AIOC Co-investor — "Contract of the Century"'],
    ['Amerada Hess / Hess', 'US Independent', '1997–2000s', 'AIOC Divestment; Delta Hess — BTC Pipeline'],
    ['SOCAR', 'National Oil Company', '1994–1998', 'AIOC Sovereign Partner — Azerbaijan'],
    ['Dana Gas PJSC', 'Regional Energy Company', '2007', 'Centurion Energy Acquirer — C$1.25B'],
    ['CNPC', 'Chinese State-Owned EPC', 'Active', 'Afghanistan Gas Pipeline & Upstream'],
    ['Sinopec', 'Chinese State-Owned EPC', 'Active', 'Afghanistan & Venezuela Downstream Infrastructure'],
    ['China Energy', 'Chinese State-Owned EPC', 'Active', 'Afghanistan & Venezuela Gas-to-Power Programs'],
];
const SUMMARY_AR = [
    ['BP', 'شركة نفط دولية كبرى', '1994–1998', 'شريك ومشغل في AIOC — حقول ACG، بحر قزوين'],
    ['UNOCAL', 'شركة أمريكية مستقلة', '1991–1998', 'تحالف استراتيجي — الدخول إلى حقول ACG؛ تأسيس AIOC'],
    ['Pennzoil', 'شركة نفط أمريكية كبرى', '1994–1998', 'شريك في AIOC — "عقد القرن"'],
    ['Amerada Hess / Hess', 'شركة أمريكية مستقلة', '1997–2000', 'التخارج من AIOC؛ دلتا-هيس — أنبوب BTC'],
    ['SOCAR', 'شركة نفط وطنية', '1994–1998', 'الشريك السيادي في AIOC — أذربيجان'],
    ['Dana Gas PJSC', 'شركة طاقة إقليمية', '2007', 'مستحوذ على سنتوريون للطاقة — 1.25 مليار دولار كندي'],
    ['CNPC', 'EPC صينية حكومية', 'نشط', 'أنبوب الغاز والقطاع العلوي في أفغانستان'],
    ['Sinopec', 'EPC صينية حكومية', 'نشط', 'البنية التحتية للقطاع السفلي في أفغانستان وفنزويلا'],
    ['China Energy', 'EPC صينية حكومية', 'نشط', 'برامج تحويل الغاز إلى طاقة في أفغانستان وفنزويلا'],
];

const Partnerships = () => {
    const { t, lang } = useLang();
    const HISTORICAL_DETAIL = lang === 'ar' ? HISTORICAL_DETAIL_AR : HISTORICAL_DETAIL_EN;
    const ACTIVE_DETAIL = lang === 'ar' ? ACTIVE_DETAIL_AR : ACTIVE_DETAIL_EN;
    const SUMMARY = lang === 'ar' ? SUMMARY_AR : SUMMARY_EN;
    return (
        <div data-testid="partnerships-page">
            <PageHero
                eyebrow={t('partnerships.hero_eyebrow')}
                title={t('partnerships.hero_title')}
                sub={t('partnerships.hero_sub')}
                image={ASSETS.handshake}
            />

            {/* Logo Grid */}
            <section className="bg-cream py-20">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <ScrollReveal>
                        <div className="text-center mb-12">
                            <div className="eyebrow mb-4">{t('partnerships.network_eyebrow')}</div>
                            <h2 className="editorial-h2 text-navy text-3xl md:text-4xl">
                                {t('partnerships.network_h2')}
                            </h2>
                        </div>
                    </ScrollReveal>

                    <div className="space-y-12">
                        <div>
                            <div className="eyebrow text-ink/55 mb-5">{t('partnerships.historical_partners')}</div>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-navy/15 border border-navy/15">
                                {PARTNERS.historical.map((p) => (
                                    <div key={p.name} className="bg-white py-8 px-4 text-center hover:bg-cream transition-colors">
                                        <div className="font-display text-navy text-lg leading-tight">{p.name}</div>
                                        <div className="text-ink/55 text-[10px] tracking-widest uppercase mt-3">{p.period}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div>
                            <div className="eyebrow text-gold mb-5">{t('partnerships.active_partners')}</div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gold/30 border border-gold/30">
                                {PARTNERS.active.map((p) => (
                                    <div key={p.name} className="bg-navy-deep py-10 px-4 text-center hover:bg-navy-mid transition-colors">
                                        <div className="font-display text-gold text-xl leading-tight">{p.name}</div>
                                        <div className="text-white/55 text-[10px] tracking-widest uppercase mt-3">{pick(p, 'role', lang)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Details with Tabs */}
            <section className="bg-white py-24">
                <div className="max-w-6xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow mb-5">{t('partnerships.details_eyebrow')}</div>
                    <h2 className="editorial-h2 text-navy text-3xl md:text-4xl mb-12">
                        {t('partnerships.details_h2')}
                    </h2>

                    <Tabs defaultValue="historical" data-testid="partnerships-tabs">
                        <TabsList className="bg-cream border border-navy/10 p-0 h-auto rounded-none">
                            <TabsTrigger
                                value="historical"
                                data-testid="tab-historical"
                                className="data-[state=active]:bg-navy data-[state=active]:text-gold rounded-none px-6 py-3 text-[12px] tracking-widest uppercase"
                            >
                                {t('partnerships.tab_historical')}
                            </TabsTrigger>
                            <TabsTrigger
                                value="active"
                                data-testid="tab-active"
                                className="data-[state=active]:bg-navy data-[state=active]:text-gold rounded-none px-6 py-3 text-[12px] tracking-widest uppercase"
                            >
                                {t('partnerships.tab_active')}
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="historical" className="mt-10 space-y-px bg-navy/10">
                            {PARTNERS.historical.map((p) => {
                                const d = HISTORICAL_DETAIL[p.name];
                                return (
                                    <div key={p.name} className="bg-white grid grid-cols-1 md:grid-cols-12 gap-6 px-6 py-8">
                                        <div className="md:col-span-4">
                                            <div className="font-display text-navy text-xl mb-1">{d?.full || p.name}</div>
                                            <div className="text-gold text-[11px] tracking-widest uppercase">{p.period}</div>
                                        </div>
                                        <div className="md:col-span-8 text-ink/75 leading-relaxed text-sm md:text-base">
                                            {d?.body || p.role}
                                        </div>
                                    </div>
                                );
                            })}
                        </TabsContent>
                        <TabsContent value="active" className="mt-10 space-y-px bg-gold/20">
                            {PARTNERS.active.map((p) => {
                                const d = ACTIVE_DETAIL[p.name];
                                return (
                                    <div key={p.name} className="bg-navy-deep grid grid-cols-1 md:grid-cols-12 gap-6 px-6 py-8">
                                        <div className="md:col-span-4">
                                            <div className="font-display text-gold text-xl mb-1">{d?.full || p.name}</div>
                                            <div className="text-white/55 text-[11px] tracking-widest uppercase">{p.role}</div>
                                        </div>
                                        <div className="md:col-span-8 text-white/75 leading-relaxed text-sm md:text-base">
                                            {d?.body || ''}
                                        </div>
                                    </div>
                                );
                            })}
                        </TabsContent>
                    </Tabs>
                </div>
            </section>

            {/* Summary Table */}
            <section className="bg-cream py-20">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow mb-5">{t('partnerships.summary_eyebrow')}</div>
                    <div className="overflow-x-auto">
                        <table className="editorial-table w-full" data-testid="partnerships-summary">
                            <thead>
                                <tr>
                                    <th>{t('partnerships.col_partner')}</th>
                                    <th>{t('partnerships.col_type')}</th>
                                    <th>{t('partnerships.col_period')}</th>
                                    <th>{t('partnerships.col_context')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {SUMMARY.map((row, i) => (
                                    <tr key={i}>
                                        <td className="font-display text-navy whitespace-nowrap">{row[0]}</td>
                                        <td className="text-ink/70 whitespace-nowrap">{row[1]}</td>
                                        <td className="text-ink/70 whitespace-nowrap">{row[2]}</td>
                                        <td className="text-ink/75">{row[3]}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Partnerships;
